import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import FeedPost from '../components/feed/FeedPost';
import api from '../lib/api';
import { useToast } from '../components/common/Toast';
import { logger } from '../lib/logger';

const POSTS_PER_PAGE = 10;

// Helper functions - defined outside component to ensure stable references
const formatDuration = (interval) => {
  if (!interval) return '';
  const match = interval.match(/(\d+):(\d+):(\d+)/);
  if (match) {
    const hours = parseInt(match[1]);
    const mins = parseInt(match[2]);
    const secs = parseInt(match[3]);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return interval;
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

const Feed = () => {
  const authUser = useSelector((state) => state.auth.user);
  const toast = useToast();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [draftRunData, setDraftRunData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const postsLengthRef = useRef(0);
  const authUserRef = useRef(authUser);
  const hasFetchedRef = useRef(false);
  
  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);
  
  useEffect(() => {
    postsLengthRef.current = posts.length;
  }, [posts.length]);

  // Format post data helper
  const formatPost = useCallback((post) => ({
    id: post.id,
    type: post.type,
    user: { 
      id: post.user?.id,
      name: post.user?.display_name || post.user?.username || 'User', 
      avatar: post.user?.avatar_url 
    },
    title: post.title || '',
    content: post.content,
    distance: post.run?.distance_km?.toString() || '',
    time: formatDuration(post.run?.duration) || '',
    pace: post.run?.pace || '',
    timestamp: formatTimeAgo(post.created_at),
    likesCount: post.likes?.length || 0,
    commentsCount: post.comments?.length || 0,
    isLiked: post.likes?.some(like => like.user_id === authUserRef.current?.id) || false,
    reactions: [],
    comments: (post.comments || []).map(c => ({
      id: c.id,
      user: c.user?.display_name || c.user?.username || 'User',
      avatar: c.user?.avatar_url || null,
      text: c.content,
      timestamp: formatTimeAgo(c.created_at)
    }))
  }), []);

  // Fetch posts from API
  const fetchPosts = useCallback(async (loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    }

    try {
      const offset = loadMore ? postsLengthRef.current : 0;
      
      const postsData = await api.posts.list(offset, POSTS_PER_PAGE);

      const formattedPosts = (postsData || []).map(formatPost);
      setHasMore(formattedPosts.length === POSTS_PER_PAGE);
      setError(null);

      if (loadMore) {
        setPosts(prev => [...prev, ...formattedPosts]);
      } else {
        setPosts(formattedPosts);
      }
    } catch (err) {
      logger.error('Error fetching posts:', err);
      if (!loadMore) {
        setError('Failed to load feed. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [formatPost]);

  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    fetchPosts(false);
  }, [fetchPosts]);

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchPosts(true);
    }
  };

  useEffect(() => {
    const draftPost = localStorage.getItem('draftPost');
    if (draftPost) {
      try {
        const postData = JSON.parse(draftPost);
        window.scrollTo(0, 0);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
        setPostContent(postData.content || '');
        if (postData.distance && postData.time && postData.pace) {
          setDraftRunData({
            title: postData.title,
            distance: postData.distance,
            time: postData.time,
            pace: postData.pace,
            runId: postData.runId
          });
        }
        setShowCreatePost(true);
        localStorage.removeItem('draftPost');
      } catch (err) {
        logger.error('Error parsing draft post:', err);
        localStorage.removeItem('draftPost');
      }
    }
  }, []);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    
    if (!postContent.trim() || !authUser?.id) return;

    setIsSubmitting(true);

    try {
      const newPostData = await api.posts.create({
        type: draftRunData ? 'run' : 'social',
        title: draftRunData?.title || null,
        content: postContent,
        run_id: draftRunData?.runId || null
      });

      // Add new post to the list
      const formattedPost = {
        id: newPostData.id,
        type: newPostData.type,
        user: { 
          id: authUser.id,
          name: newPostData.user?.display_name || authUser.name || 'You', 
          avatar: newPostData.user?.avatar_url || authUser.avatar_url
        },
        title: newPostData.title || '',
        content: newPostData.content,
        distance: draftRunData?.distance || '',
        time: draftRunData?.time || '',
        pace: draftRunData?.pace || '',
        timestamp: 'Just now',
        likesCount: 0,
        commentsCount: 0,
        isLiked: false,
        reactions: [],
        comments: []
      };

      setPosts([formattedPost, ...posts]);
      
      // Reset form
      setPostContent('');
      setDraftRunData(null);
      setShowCreatePost(false);
      toast.success('Post created successfully!');
    } catch (error) {
      logger.error('Error creating post:', error);
      toast.error('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 max-w-2xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-primary-pink animate-spin mb-4" />
          <p className="text-gray-600">Loading feed...</p>
        </div>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="p-4 max-w-2xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="scrapbook-container text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
            Couldn't load feed
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              fetchPosts(false);
            }}
            className="px-6 py-2 bg-gradient-vibe text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-semibold mb-2 bg-gradient-vibe bg-clip-text text-transparent">
          Your ZoneRuns Feed
        </h1>
        <p className="text-gray-600 text-body">
          See what your friends are up to!
        </p>
      </div>

      {showCreatePost && (
        <div className="overflow-hidden mb-6">
          <form onSubmit={handleCreatePost} className="scrapbook-container">
            <h2 className="font-heading font-semibold text-lg mb-4">
              {draftRunData ? draftRunData.title : "What's on your mind?"}
            </h2>
            
            {draftRunData && (
              <div className="mb-4 bg-gradient-vibe-subtle rounded-2xl p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="font-bold text-lg text-primary-pink">
                      {draftRunData.distance}
                    </div>
                    <div className="text-xs text-gray-600">km</div>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-secondary-blue">
                      {draftRunData.time}
                    </div>
                    <div className="text-xs text-gray-600">time</div>
                  </div>
                  <div>
                    <div className="font-bold text-lg text-primary-yellow">
                      {draftRunData.pace}
                    </div>
                    <div className="text-xs text-gray-600">pace</div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-4">
              <textarea
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                placeholder="Share your thoughts, achievements, or motivation..."
                rows="5"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-pink focus:border-transparent transition-all resize-none"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={!postContent.trim() || isSubmitting}
              className={`w-full py-3 rounded-xl font-semibold transition-all ${
                postContent.trim() && !isSubmitting
                  ? 'bg-gradient-vibe text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  Posting...
                </span>
              ) : (
                'Post to Feed'
              )}
            </button>
          </form>
        </div>
      )}
      
      <div className="space-y-6">
        {posts.length === 0 ? (
          <div className="scrapbook-container text-center py-12">
            <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">
              No posts yet
            </h3>
            <p className="text-gray-600 mb-4">
              Be the first to share something with the community!
            </p>
            <button
              onClick={() => setShowCreatePost(true)}
              className="px-6 py-2 bg-gradient-vibe text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Create a Post
            </button>
          </div>
        ) : (
          <>
            {posts.map((post) => (
              <div key={post.id}>
                <FeedPost 
                  post={post} 
                  onUpdatePost={(updatedPost) => {
                    setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
                  }}
                />
              </div>
            ))}
            
            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="px-8 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70"
                >
                  {isLoadingMore ? (
                    <span className="flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    'Load More Posts'
                  )}
                </button>
              </div>
            )}
            
            {!hasMore && posts.length > 0 && (
              <div className="text-center py-6 text-gray-500 text-sm">
                You've reached the end of the feed
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={() => setShowCreatePost(!showCreatePost)}
        className="fixed bottom-6 right-6 w-12 h-12 flex items-center justify-center bg-gradient-vibe text-white rounded-full shadow-lg hover:shadow-xl hover:scale-[1.1] active:scale-[0.9] transition-all z-50"
      >
        <FontAwesomeIcon icon={showCreatePost ? faTimes : faPlus} className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Feed;
